import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { GET_KEY_SHORTCUTS } from "../../../../utils/GraphQL/queries";
import {
  DELETE_KEY_SHORTCUT,
  CREATE_KEY_SHORTCUT,
} from "../../../../utils/GraphQL/mutations";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import "./shortcutsPage.css";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";

const blankShortcut = {
  title: "",
  text: "",
};

const ShortcutsPage = () => {
  const { handleSnack } = useSnackbar();
  const [shortcuts, setShortcuts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [newShortcut, setNewShortcut] = useState({ ...blankShortcut });
  const [errors, setErrors] = useState({});
  const [shortcutsFetch] = useLazyQuery(GET_KEY_SHORTCUTS);
  const [deleteCall] = useMutation(DELETE_KEY_SHORTCUT);
  const [createCall] = useMutation(CREATE_KEY_SHORTCUT);

  useEffect(() => {
    window.scrollTo(0, 0);
    shortcutsCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shortcutsCall = async () => {
    const { data } = await shortcutsFetch({
      fetchPolicy: "network-only",
    });

    if (data?.getKeyShortcuts) {
      setShortcuts([...data?.getKeyShortcuts]);
    }
  };

  // Per-field messages rather than one snackbar, so each input says what is
  // wrong with it — matches the other Defaults tabs.
  const validate = () => {
    const next = {};
    if (!newShortcut?.title?.trim()) next.title = "Enter a title.";
    if (!newShortcut?.text?.trim()) next.text = "Enter the shortcut text.";
    return next;
  };

  const addItem = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const { data } = await createCall({
      variables: {
        ...newShortcut,
      },
    });

    if (data?.createKeyShortcut) {
      handleSnack("Shortcut Created", 2000);
      setShortcuts((prev) => [...prev, data?.createKeyShortcut]);
      handleCloseModal();
      return;
    }

    handleSnack("Something went wrong...Please try again", 2000);
  };

  const handleChange = async (event) => {
    const name = event?.target?.name;
    const value = event?.target?.value;

    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });

    setNewShortcut((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setNewShortcut({ ...blankShortcut });
    setErrors({});
  };

  const deleteItem = async (id) => {
    const { data } = await deleteCall({
      variables: {
        id,
      },
    });

    if (data?.deleteKeyShortcut === true) {
      handleSnack("Shortcut Deleted", 2000);
      setShortcuts((prev) => {
        const filtered = prev?.filter((item) => item.id !== id);
        return [...filtered];
      });
    }
  };

  return (
    <div className="bdr-page">
      <div className="bdr-panel bdr-grad-container-med bdr-border-std bdr-shadow-depth">
        <div className="bdr-row-end">
          <BdrButton
            variant="confirm"
            size="sm"
            icon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => setModalOpen(true)}
          >
            New Shortcut
          </BdrButton>
        </div>
        <table className="bdr-table">
          <thead>
            <tr>
              <th scope="col">Title</th>
              <th scope="col">Text</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shortcuts?.length > 0 &&
              shortcuts?.map((item) => {
                return (
                  <tr key={item?.id}>
                    <td data-label="Title">{item?.title}</td>
                    <td data-label="Text">{item?.text} </td>
                    <td className="bdr-table-actions">
                      <BdrButton
                        variant="plain"
                        size="sm"
                        iconOnly
                        icon={<DeleteIcon />}
                        className="bdr-text-danger"
                        ariaLabel="Delete shortcut"
                        confirm
                        confirmTitle="Delete this shortcut?"
                        confirmLabel="Delete"
                        note={`"${item?.title}" will be removed for everyone. This can't be undone.`}
                        onClick={() => deleteItem(item?.id)}
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <BdrModal
        open={modalOpen}
        onClose={handleCloseModal}
        title="New Shortcut"
        variant="panel"
        size="sm"
        actions={
          <>
            <BdrButton onClick={handleCloseModal}>Cancel</BdrButton>
            {/* solid: this modal sits on the saturated --bdr-grad-panel */}
            <BdrButton variant="confirm-solid" onClick={addItem}>
              Save Shortcut
            </BdrButton>
          </>
        }
      >
        <div className="shortcuts-modal-content">
          <BdrInput
            label="Title"
            name="title"
            error={errors.title}
            value={newShortcut?.title}
            onChange={handleChange}
            placeholder="Shortcut title..."
          />

          <BdrInput
            label="Text"
            name="text"
            error={errors.text}
            value={newShortcut?.text}
            onChange={handleChange}
            placeholder="Shortcut text..."
            multiline
            rows={4}
          />
        </div>
      </BdrModal>
    </div>
  );
};

export default ShortcutsPage;
